/**
 * Brand tokens for custom components (owner feedback F3 and F21). A component follows the workspace's brand kit
 * because every colour, font, radius, type size and shadow it uses is a design token (`var(--accent)`): the app
 * repaints the tokens with the brand kit's accent and fonts, light and dark. A raw `#hex`, `rgb()`, colour name, font
 * family name, or a pixel value that a token already stands for freezes the look and ignores the brand, so the
 * stylesheet validator flags it and names the token to use. Pure and browser-safe.
 */

/** The light values of the colour tokens a stylesheet may stand in for (nearest match is suggested). */
const COLOUR_TOKENS: Record<string, string> = {
  ink: '#101828',
  'ink-2': '#475467',
  'ink-3': '#667085',
  'ink-4': '#98a2b3',
  border: '#e4e7ec',
  'border-strong': '#d0d5dd',
  'border-subtle': '#f2f4f7',
  canvas: '#f7f8fa',
  surface: '#ffffff',
  subtle: '#f9fafb',
  'neutral-chip': '#f2f4f7',
  accent: '#444ce7',
  'accent-hover': '#3538cd',
  'accent-tint': '#eef4ff',
  'accent-border': '#c7d7fe',
  'accent-soft': '#f5f8ff',
  info: '#175cd3',
  'info-bg': '#eff8ff',
  ok: '#067647',
  'ok-bg': '#ecfdf3',
  'ok-strong': '#17b26a',
  warn: '#b54708',
  'warn-bg': '#fffaeb',
  'warn-strong': '#f79009',
  err: '#b42318',
  'err-bg': '#fef3f2',
  'err-strong': '#d92d20',
  comp: '#5925dc',
  'comp-bg': '#f4f3ff',
  ext: '#b93815',
  'ext-bg': '#fef6ee',
  inverse: '#101828',
};

/** Colour names that have an obvious token. */
const NAME_TOKEN: Record<string, string> = {
  white: 'surface',
  black: 'ink',
  red: 'err',
  crimson: 'err',
  green: 'ok',
  lime: 'ok-strong',
  blue: 'info',
  navy: 'info',
  indigo: 'accent',
  purple: 'comp',
  violet: 'comp',
  orange: 'warn-strong',
  amber: 'warn-strong',
  yellow: 'warn-strong',
  gold: 'warn-strong',
  gray: 'ink-3',
  grey: 'ink-3',
  silver: 'border-strong',
  lightgray: 'border',
  lightgrey: 'border',
  whitesmoke: 'canvas',
  teal: 'ok',
};

function parseColour(v: string): [number, number, number] | null {
  const s = v.trim().toLowerCase();
  let m = /^#([0-9a-f]{3,8})$/.exec(s);
  if (m) {
    const h = m[1]!;
    if (h.length === 3 || h.length === 4)
      return [0, 1, 2].map((i) => parseInt(h[i]! + h[i]!, 16)) as [number, number, number];
    if (h.length === 6 || h.length === 8)
      return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
    return null;
  }
  m = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/.exec(s);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
}

/** The token whose light value is closest to a raw colour (hex, rgb) or named colour; null when there is no reasonable one. */
export function nearestColourToken(value: string): string | null {
  const named = NAME_TOKEN[value.trim().toLowerCase()];
  if (named) return named;
  const rgb = parseColour(value);
  if (!rgb) return null;
  let best: string | null = null;
  let bestD = Infinity;
  for (const [token, hex] of Object.entries(COLOUR_TOKENS)) {
    const t = parseColour(hex)!;
    const d = (t[0] - rgb[0]) ** 2 + (t[1] - rgb[1]) ** 2 + (t[2] - rgb[2]) ** 2;
    if (d < bestD) {
      bestD = d;
      best = token;
    }
  }
  return best;
}

const RADIUS_TOKEN: Record<string, string> = {
  '6px': 'radius-sm',
  '8px': 'radius',
  '12px': 'radius-card',
  '14px': 'radius-modal',
  '16px': 'radius-sheet',
  '999px': 'radius-pill',
  '9999px': 'radius-pill',
};
const SIZE_TOKEN: Record<string, string> = {
  '11px': 'text-11',
  '12px': 'text-12',
  '13px': 'text-13',
  '14px': 'text-14',
  '16px': 'text-16',
  '20px': 'text-20',
  '24px': 'text-24',
  '32px': 'text-32',
};
const SHADOW_TOKEN: Record<string, string> = { sm: 'shadow-xs', md: 'shadow-card', lg: 'shadow-pop' };

export type BrandProblem = { message: string; token?: string };

/**
 * Why a declaration ignores the brand, with the token to use (null: it follows the brand). Colours themselves are
 * reported by the value check; this covers font families, type sizes, radii and shadows with a literal value.
 */
export function brandProblem(prop: string, raw: string): BrandProblem | null {
  const v = raw.trim();
  const low = v.toLowerCase();
  if (prop === 'font-family' || prop === 'font') {
    if (/^(inherit|initial|unset)$/.test(low) || /var\(\s*--font-(sans|mono|heading)\s*\)/.test(low)) {
      return null;
    }
    if (prop === 'font' && /^(inherit|initial|unset)$/.test(low)) return null;
    const mono = /mono|courier|consolas|menlo|monospace/.test(low);
    const token = mono ? 'font-mono' : 'font-sans';
    return {
      message: `${prop}: use var(--${token}), the workspace's brand font, instead of a font name`,
      token,
    };
  }
  if (prop === 'font-size') {
    const t = SIZE_TOKEN[low];
    if (t) return { message: `font-size: use var(--${t}) instead of ${v}`, token: t };
  }
  if (prop === 'border-radius' || /^border-(top|bottom)-(left|right)-radius$/.test(prop)) {
    const t = RADIUS_TOKEN[low];
    if (t) return { message: `${prop}: use var(--${t}) instead of ${v}`, token: t };
  }
  if (prop === 'box-shadow' && !/^var\(\s*--shadow-[a-z-]+\s*\)$/.test(low) && low !== 'none') {
    // A shadow with a blur is a design decision the tokens already make (rings of 0 blur have no token).
    const blur = Number(/^(?:inset\s+)?-?\d+(?:px)?\s+-?\d+(?:px)?\s+(\d+)px/.exec(low)?.[1] ?? 0);
    if (blur > 0) {
      const t = blur <= 3 ? SHADOW_TOKEN.sm! : blur <= 12 ? SHADOW_TOKEN.md! : SHADOW_TOKEN.lg!;
      return { message: `box-shadow: use var(--${t}) instead of a literal shadow`, token: t };
    }
  }
  return null;
}

/** The brand tokens with what they are, for the catalog's guide (names stay the same, values follow the brand kit). */
export const BRAND_TOKEN_GROUPS: Record<string, readonly string[]> = {
  'Text and lines': ['ink', 'ink-2', 'ink-3', 'ink-4', 'border', 'border-strong', 'border-subtle'],
  Surfaces: ['canvas', 'surface', 'subtle', 'neutral-chip', 'inverse', 'on-inverse'],
  'Brand accent (repainted by the brand kit)': [
    'accent',
    'accent-hover',
    'accent-ink',
    'accent-tint',
    'accent-border',
    'accent-soft',
    'on-accent',
    'focus-ring',
  ],
  Status: [
    'info',
    'info-bg',
    'ok',
    'ok-bg',
    'warn',
    'warn-bg',
    'err',
    'err-bg',
    'ext',
    'ext-bg',
    'comp',
    'comp-bg',
  ],
  'Type, radius, shadow': [
    'font-sans',
    'font-mono',
    'text-11',
    'text-12',
    'text-13',
    'text-14',
    'text-16',
    'text-20',
    'text-24',
    'text-32',
    'radius-sm',
    'radius',
    'radius-card',
    'radius-modal',
    'radius-pill',
    'shadow-xs',
    'shadow-card',
    'shadow-raised',
    'shadow-pop',
  ],
};

/** Light-theme values of a token, for the catalog (the brand kit overrides the accent group). */
export function tokenLightValue(token: string): string | null {
  return COLOUR_TOKENS[token] ?? null;
}
